import { useApolloClient } from '@apollo/client/react'
import { useCallback } from 'react'

import { useAuth } from '@/auth/useAuth'
import { CommunityShowView_CommunityDocument } from '@/graphql/api/generated'
import { usePrefetchOnIntent } from '@/hooks/usePrefetchOnIntent'
import { CommunitiesRoutes } from '@/routes/_hooks/routes.enum'
import { generatePath } from '@/routing'

/**
 * Warms the community detail query on hover/focus intent so opening a community
 * from a list resolves from the Apollo cache instantly. Mirrors exactly the
 * query + variables CommunitiesShowView runs (same cache key, including the
 * current-user-dependent fields), deduped per slug+viewer, cache-first. Timing
 * and cleanup live in usePrefetchOnIntent.
 */
const prefetched = new Set<string>()

export function usePrefetchCommunity(slug?: string) {
  const client = useApolloClient()
  const auth = useAuth()
  const userSlug = auth.user?.slug

  const run = useCallback(() => {
    if (!slug) return
    // The cache key depends on the viewer; dedup on both.
    const dedupKey = `${slug}::${userSlug ?? ''}`
    if (prefetched.has(dedupKey)) return
    prefetched.add(dedupKey)
    void client
      .query({
        query: CommunityShowView_CommunityDocument,
        variables: {
          id: generatePath(CommunitiesRoutes.IRI, { slug }),
          statuses: ['ok'],
          currentUserStatuses: ['ok', 'pending', 'invited'],
          currentUserSlug: userSlug ?? undefined,
          withCurrentUserMemberships: !!userSlug,
        },
        fetchPolicy: 'cache-first',
      })
      .catch(() => {
        prefetched.delete(dedupKey)
      })
  }, [client, slug, userSlug])

  return usePrefetchOnIntent(run)
}
